import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * Retention: the cohort against what survived, with the rate called out.
 *
 * Two bars rather than one, because a rate alone hides its own denominator —
 * "60%" reads very differently over four services than over four hundred. The
 * rate is labelled above the pair so the board gets the headline without losing
 * the size behind it.
 */
export default function CohortBarChart({ groups = [], measure = "units", formatValue, onGroupClick }) {
  const isMoney = measure !== "units";
  const format =
    formatValue ||
    ((value) =>
      isMoney
        ? new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(value)
        : new Intl.NumberFormat("en-GB").format(value));

  const cohortLabel = measure === "nrr" ? "Cohort then" : "Cohort";
  const retainedLabel = measure === "nrr" ? "Survivors now" : "Retained";

  const data = groups.map((group) => ({
    name: group.label,
    cohort: group.cohort,
    retained: group.retained,
    rate: group.rate,
  }));

  if (!data.length) {
    return <p className="py-12 text-center text-sm text-[#4a5b68]">No records in this cohort.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={340} minWidth={280}>
      <BarChart data={data} margin={{ top: 30, right: 16, left: -8, bottom: 0 }}>
        <CartesianGrid stroke="#dddddd" vertical={false} />
        <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fill: "#4a5b68", fontSize: 12 }} />
        <YAxis tickLine={false} axisLine={false} tick={{ fill: "#4a5b68", fontSize: 12 }} tickFormatter={format} />
        <Tooltip
          cursor={{ fill: "rgba(15, 23, 42, 0.04)" }}
          contentStyle={{ borderRadius: 4, borderColor: "#cccccc", boxShadow: "0 4px 12px rgba(51, 71, 91, 0.12)" }}
          formatter={(value, name) => [format(value), name === "cohort" ? cohortLabel : retainedLabel]}
        />
        <Legend
          wrapperStyle={{ fontSize: 12, color: "#4a5b68" }}
          formatter={(value) => (value === "cohort" ? cohortLabel : retainedLabel)}
        />
        <Bar dataKey="cohort" fill="#cccccc" maxBarSize={40} radius={[2, 2, 0, 0]} isAnimationActive={false} />
        <Bar
          dataKey="retained"
          fill="#50e3c2"
          maxBarSize={40}
          radius={[2, 2, 0, 0]}
          isAnimationActive={false}
          cursor={onGroupClick ? "pointer" : undefined}
          onClick={(entry) => onGroupClick && onGroupClick(entry?.name ?? entry?.payload?.name)}
        >
          {/* The rate sits above the retained bar: the headline, next to the
              size that produced it rather than instead of it. */}
          <LabelList
            dataKey="rate"
            position="top"
            fill="#323c44"
            fontSize={12}
            fontWeight={700}
            formatter={(value) => (value === null || value === undefined ? "" : `${value}%`)}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
